import React, { useEffect, useState } from 'react';
import { Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { IconEngine, IconGauge, IconClock, IconTruckOff } from '@tabler/icons-react';
import StaticMap from '../../components/StaticMap';
import { getHeavyDutyMachineryById } from '../../services/HeavyDutyMachineryService';
import { onTelemetryUpdate } from '../../services/RealtimeService';
import { IBooking } from '../../interfaces/heavyDutyMachinery/IBooking';
import { IFullMachineryTelemetry, isPublicMachineryTelemetry } from '../../interfaces/heavyDutyMachinery/ITelemetry';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { pillToneStyle, SURFACE_1_SOFT } from '../../theme/creamyTokens';

const { Title, Text } = Typography;

interface ActiveRentalCardProps {
  booking: IBooking;
}

/**
 * "Mi máquina alquilada ahora" -- shown above the bookings list only while
 * `booking` (an in-progress, Confirmed rental -- see MyBookings' own
 * activeBooking lookup) is live. Telemetry comes from GetSpaceById
 * (getHeavyDutyMachineryById), which already applies ITelemetryVisibilityPolicy
 * server-side: an ActiveCustomer sees the full shape while the booking is
 * in progress, and the backend itself drops back to the Public shape the
 * moment it isn't (completed/cancelled) -- so this card never adds its own
 * expiry/hiding logic, it just renders whatever shape comes back.
 */
const ActiveRentalCard: React.FC<ActiveRentalCardProps> = ({ booking }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [telemetry, setTelemetry] = useState<IFullMachineryTelemetry | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    getHeavyDutyMachineryById(booking.heavyDutyMachineryId).then((machine) => {
      if (cancelled || !machine?.telemetry) return;
      if (!isPublicMachineryTelemetry(machine.telemetry)) setTelemetry(machine.telemetry);
    });
    return () => {
      cancelled = true;
    };
  }, [booking.heavyDutyMachineryId]);

  useEffect(
    () =>
      onTelemetryUpdate((payload: IFullMachineryTelemetry) => {
        if (payload.machineryId === booking.heavyDutyMachineryId) setTelemetry(payload);
      }),
    [booking.heavyDutyMachineryId]
  );

  const hasLiveData = !!telemetry && (telemetry.latitude != null || telemetry.engineHours != null || telemetry.ignitionOn != null);

  return (
    <div
      style={{
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: 16,
        padding: 16,
        marginBottom: 24,
      }}
    >
      <Title level={5} style={{ margin: '0 0 2px' }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACTIVE_RENTAL_CARD_TITLE)}
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
        {booking.heavyDutyMachineryTitle}
      </Text>

      {!hasLiveData ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '14px 12px',
            borderRadius: 12,
            background: SURFACE_1_SOFT,
            color: token.colorTextTertiary,
          }}
        >
          <IconTruckOff size={18} />
          <Text type="secondary" style={{ fontSize: 13 }}>
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACTIVE_RENTAL_NO_LIVE_DATA)}
          </Text>
        </div>
      ) : (
        <>
          {telemetry?.latitude != null && telemetry?.longitude != null && (
            <div style={{ marginBottom: 10 }}>
              <StaticMap latitude={telemetry.latitude} longitude={telemetry.longitude} width={400} height={160} />
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 12px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
                ...pillToneStyle(telemetry?.ignitionOn ? 'success' : 'default', token),
              }}
            >
              <IconEngine size={14} />
              {translate(
                t,
                telemetry?.ignitionOn ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_FLEET_ENGINE_ON : TRANSLATIONS.HEAVY_DUTY_MACHINERY_FLEET_ENGINE_OFF
              )}
            </span>
            {telemetry?.engineHours != null && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: token.colorTextSecondary }}>
                <IconClock size={14} />
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACTIVE_RENTAL_HOURS_CONSUMED_LABEL)}: {telemetry.engineHours.toFixed(1)}h
              </span>
            )}
            {telemetry?.fuelPercent != null && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: token.colorTextSecondary }}>
                <IconGauge size={14} />
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FLEET_FUEL_LABEL)}: {Math.round(telemetry.fuelPercent)}%
              </span>
            )}
          </div>
          {telemetry?.lastUpdateUtc && (
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FLEET_LAST_UPDATE_LABEL)}: {new Date(telemetry.lastUpdateUtc).toLocaleString()}
            </Text>
          )}
        </>
      )}
    </div>
  );
};

export default ActiveRentalCard;
